import type { GetEntityMixQuery } from '@lib/graphql/generated';
import { GetEntityMixDocument } from '@lib/graphql/generated';
import type { TvmEntityType } from '@lib/graphql/types';
import { usePaginatedQuery } from '@lib/graphql/urql';
import type { PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { EntityMixGrid } from './EntityMixGrid';

type EntityMixEntry = Extract<
  NonNullable<NonNullable<GetEntityMixQuery['entities']>['data']>[number],
  { __typename: 'Movie' | 'Show' }
>;

// `entities` is a Movie | Show | Vod union. Vods are never returned for the filters
// this component sends, and the query selects nothing for them, so they are dropped
// instead of rendered as a teaser without a title or a working link.
const isMovieOrShow = (entry: unknown): entry is EntityMixEntry =>
  (entry as EntityMixEntry)?.__typename === 'Movie' || (entry as EntityMixEntry)?.__typename === 'Show';

// Stable key for a set of query variables, independent of key order.
const toVariablesKey = (variables: unknown) =>
  JSON.stringify(variables, (_key, value) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
      : value,
  );

export interface EntityMixVariables {
  first: number;
  page: number;
  /**
   * Provider **slug** - the `entities` query resolves providers by slug, not by id.
   *
   * Do not filter by provider without also setting `type`: `entities(provider:)`
   * across both types takes 23-50s upstream (and 503s behind Varnish), while the
   * same query with `type` takes ~2.5s. That is why the provider page still uses
   * `EntityMixLegacy`.
   */
  provider?: string;
  /** Genre ids. */
  genres?: string[];
  /** Person id. */
  person?: string;
  type?: TvmEntityType;
  orderBy?: string;
  orderDirection?: string;
}

interface EntityMixProps extends PropsWithChildren, PropsWithClassProps {
  variables: EntityMixVariables;
  skip?: boolean;
}

/**
 * Movie/show grid backed by the single `entities` query, which mixes and sorts both
 * types server-side. Used by the genre and person pages.
 */
export const EntityMix = ({ variables, skip, children, classProps }: EntityMixProps) => {
  const [{ data, fetching, hasMorePages, fetchNextPage }] = usePaginatedQuery({
    query: GetEntityMixDocument,
    variables,
    pause: skip,
  });

  // The paginated hook keeps the pages it already loaded while the next one is in
  // flight. That is what should stay on screen for "Mehr laden", but after a filter
  // or sort change those pages belong to the previous query, so they are dropped
  // until the hook has reset to the new variables.
  const variablesKey = toVariablesKey(variables);
  const [appliedVariablesKey, setAppliedVariablesKey] = useState(variablesKey);
  const hasStalePages = appliedVariablesKey !== variablesKey;

  useEffect(() => {
    setAppliedVariablesKey(variablesKey);
  }, [variablesKey]);

  const entities = useMemo(() => {
    if (hasStalePages || skip) return [];
    const entries = data?.flatMap((page) => page?.entities?.data ?? []).filter(isMovieOrShow) ?? [];
    // Tie-broken sorts (e.g. tmdb_score) can repeat an entry across pages, which
    // would otherwise produce duplicate React keys.
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [hasStalePages, skip, data]);

  return (
    <EntityMixGrid
      entities={entities}
      fetching={fetching}
      hasMorePages={hasMorePages && !skip}
      onLoadMore={fetchNextPage}
      classProps={classProps}
    >
      {children}
    </EntityMixGrid>
  );
};
